import React, {useEffect, useState} from "react";
import GenericItem from "../../common/items/GenericItem";
import BackButton from "../../common/buttons/BackButton";
import {PAYMENT_TYPE, UPDATE_SUB_ACTION} from "../../../constants";
import GenericButton from "../../common/buttons/GenericButton";
import PlanSelectItem from "../../common/subscriptions/PlanSelectItem";
import {
    daysBetween,
    findLevel,
    getAmountLabel, getFormattedMessage,
    getMinMaxCount,
    setSelectedPlanAsOriginal
} from "../../../helpers/helpers";
import PlanSelectUpdateContainer from "../../common/subscriptions/PlanSelectUpdateContainer";
import PlanUpdateSubscriptionRow from "../../common/subscriptions/PlanUpdateSubscriptionRow";

const UpdateSubscriptionPage = (props: {
    pages: Object,
    data: Object,
    setData: Function,
    payNow: boolean,
    setPage: Function,
    selectedPlan: Object,
    setSelectedPlan: Function,
    getTierPlanAtDifferentRecurrence: Function,
    paymentType: number
}) => {
    const {data, setPage, pages,
        selectedPlan, setSelectedPlan,
        getTierPlanAtDifferentRecurrence, paymentType} = props;

    const {
        doNotRenew,
        levels = [],
        level,
        allPaymentMethods,
        expirationDate,
        currentCountOfInspectors,
        hasActiveFreeTrial } = data || {};

    const isVDSSubscription = paymentType === PAYMENT_TYPE.VDS

    const daysUntilExpired = daysBetween(new Date(), new Date(expirationDate));

    const [pageType, setPageType] = useState(UPDATE_SUB_ACTION.SPLASH);
    const [selectedLevelValue, setSelectedLevelValue] = useState(null);
    const [isSelectedPlanMaxDelimiterCount, setisSelectedPlanMaxDelimiterCount] = useState(false);
    const [isSelectedPlanMinDelimiterCount, setisSelectedPlanMinDelimiterCount] = useState(false);

    useEffect(() => {
        if (pageType === UPDATE_SUB_ACTION.SPLASH) {
            setSelectedLevelValue(null);
            setSelectedPlanAsOriginal(levels, level, setSelectedPlan);
        }
    }, [pageType]);

    useEffect(() => {
        if (!selectedPlan)
            return;

        if (isVDSSubscription) {
            const {min, max} = getMinMaxCount(levels, 'maxScanCount');
            setisSelectedPlanMaxDelimiterCount(selectedPlan.maxScanCount === max);
            setisSelectedPlanMinDelimiterCount(selectedPlan.maxScanCount === min);
        } else {
            const {min, max} = getMinMaxCount(levels, 'maxInspectorCount');
            setisSelectedPlanMaxDelimiterCount(selectedPlan.maxInspectorCount === max);
            setisSelectedPlanMinDelimiterCount(selectedPlan.maxInspectorCount === min);
        }
    }, [selectedPlan]);

    const onBackClick = () => {
        if (pageType === UPDATE_SUB_ACTION.SPLASH) {
            setPage(pages.SPLASH);
            return;
        }
        setPageType(UPDATE_SUB_ACTION.SPLASH);
    };

    const updatePlanToNewSelectedLevel = () => {
        const selectedLevel = findLevel(levels, selectedLevelValue);

        if (pageType === UPDATE_SUB_ACTION.DOWNGRADE) {
            setSelectedPlan({
                ...selectedLevel,
                oldLabel: selectedPlan.label,
                oldPriceAmount: selectedPlan.priceAmount,
                oldMonths: selectedPlan.months,
                downgrade: true,
                upgrade: false,
                changeRecurrence: false
            });
        } else if (pageType === UPDATE_SUB_ACTION.UPGRADE) {
            setSelectedPlan({
                ...selectedLevel,
                oldLabel: selectedPlan.label,
                oldPriceAmount: selectedPlan.priceAmount,
                oldMonths: selectedPlan.months,
                downgrade: false,
                upgrade: true,
                changeRecurrence: false
            });
        } else if (pageType === UPDATE_SUB_ACTION.RECURRENCE) {
            const newPlan = getTierPlanAtDifferentRecurrence(levels, selectedPlan);
            setSelectedPlan({
                ...newPlan,
                downgrade: false,
                upgrade: false,
                changeRecurrence: true
            });
        }
    }

    const onContinueClick = () => {
        updatePlanToNewSelectedLevel();
        setPage(pages.UPDATE_SUBSCRIPTION_CONFIRM);
    };

    let isCurrentCountMoreThanMaxCountOfSelectedLevel = false;

    let Body = <></>;
    if (allPaymentMethods.length === 0 || daysUntilExpired <= 0) {
        const isExpired = daysUntilExpired <= 0;
        const hasNoPaymentMethods = allPaymentMethods.length === 0;

        let updateText;
        if (isExpired) {
            updateText = i18n.payments.sub.updateSubHasExpired;
        } else if (hasNoPaymentMethods) {
            updateText = i18n.payments.sub.updateSubHasNoPaymentMethod;
        }

        Body = (
            <div className={'flexColumn update-subscription-body-div update-subscription-message'}
                 data-testid={'updateSubscriptionCannotWorkTextDiv'}>
                <p>{updateText}</p>
            </div>
        );
    } else if (pageType === UPDATE_SUB_ACTION.SPLASH) {
        Body = (
            <PlanUpdateSubscriptionRow setPageType={setPageType}
                                       showUpgrade={!isSelectedPlanMaxDelimiterCount}
                                       testid={'splashUpdateSubscriptionRowDiv'}
                                       showDowngrade={!isSelectedPlanMinDelimiterCount}/>
        );
    } else if (pageType === UPDATE_SUB_ACTION.DOWNGRADE) {
        const selectedLevel = levels[selectedLevelValue];
        isCurrentCountMoreThanMaxCountOfSelectedLevel = selectedLevel && !isVDSSubscription && currentCountOfInspectors > selectedLevel.maxInspectorCount;

        Body = (
            <div className={'flexColumn update-subscription-body-div'}>
                <div className={`plan-item flexColumn`}>
                    <div className={'plan-sub-title'}>
                        {i18n.payments.downgrade}
                    </div>
                    <div className={'plan-sub-details'}>
                        {i18n.payments.sub.selectSubToDowngradeTo}
                    </div>
                </div>

                {
                    isCurrentCountMoreThanMaxCountOfSelectedLevel && (
                        <GenericItem title={
                            getFormattedMessage(i18n.payments.vri.upgradeSubWouldExceedMaxInspectorCountWarning, currentCountOfInspectors, selectedLevel.maxInspectorCount)
                        } testid={'upgradeSubWouldExceedMaxInspectorCountWarning'}/>
                    )
                }

                <PlanSelectUpdateContainer isUpgrade={false}
                                           levels={levels}
                                           doNotRenew={doNotRenew}
                                           testid={'downgradeSubscriptionUpdateDiv'}
                                           selectedLevelValue={selectedLevelValue}
                                           setSelectedLevelValue={setSelectedLevelValue}
                                           paymentType={paymentType}
                                           selectedPlan={selectedPlan}/>
            </div>
        );
    } else if (pageType === UPDATE_SUB_ACTION.UPGRADE) {
        const details = hasActiveFreeTrial
            ? i18n.payments.sub.selectSubToUpgradeToFreeTrial
            : i18n.payments.sub.selectSubToUpgradeTo;

        Body = (
            <div className={'flexColumn update-subscription-body-div'}>
                <GenericItem title={i18n.payments.upgrade}
                             details={details}/>

                <PlanSelectUpdateContainer isUpgrade={true}
                                           levels={levels}
                                           doNotRenew={doNotRenew}
                                           testid={'upgradeSubscriptionUpdateDiv'}
                                           selectedLevelValue={selectedLevelValue}
                                           setSelectedLevelValue={setSelectedLevelValue}
                                           paymentType={paymentType}
                                           selectedPlan={selectedPlan}/>
            </div>
        );
    } else if (pageType === UPDATE_SUB_ACTION.RECURRENCE) {
        const monthly = selectedPlan?.months === 1;
        const details = monthly ? i18n.payments.sub.changeRecurrenceFromMonthlyToAnnual
            : i18n.payments.sub.changeRecurrenceFromAnnualToMonthly;

        const newPlan = getTierPlanAtDifferentRecurrence(levels, selectedPlan);
        if (!newPlan) {
            console.error('Failed to get same tier plan at different recurrence');
            return <></>;
        }

        const newPlanSelectLabel = `${newPlan?.label} ${newPlan.months === 1 ? `(${i18n.payments.monthly})` : `(${i18n.payments.annual})`}`

        Body = (
            <div className={'flexColumn update-subscription-body-div'} data-testid={'updateRecurrenceSubscriptionUpdateDiv'}>
                <div className={`plan-item flexColumn`}>
                    <div className={'plan-sub-title'}>
                        {i18n.payments.recurrence} {details}
                    </div>
                </div>

                <PlanSelectItem selected={true}
                                label1={newPlanSelectLabel}
                                label2={getAmountLabel(newPlan.priceAmount, newPlan.months)}
                                doNotRenew={doNotRenew}
                                readOnly={true}/>
            </div>
        );
    }

    const planSelectLabel = `${selectedPlan?.label} ${selectedPlan?.months === 1 ? `(${i18n.payments.monthly})` : `(${i18n.payments.annual})`}`
    const showContinueBtn = selectedLevelValue !== null || pageType === UPDATE_SUB_ACTION.RECURRENCE;

    if (!data)
        return <></>;

    return (
        <div className={'row'} data-testid={'updateSubscriptionPageDiv'}>
            <div className={'col-12'}>
                <div style={{display: "grid", gridAutoFlow: "column"}}>
                    <div style={{display: "grid", height: "fit-content"}}>
                        <div className={`plan-item flexColumn`}>
                            <div className={'plan-sub-title'}>
                                {`Current Subscription`}
                            </div>
                        </div>
                        <PlanSelectItem selected={true}
                                        label1={planSelectLabel}
                                        label2={getAmountLabel(selectedPlan?.priceAmount, selectedPlan?.months)}
                                        doNotRenew={doNotRenew}
                                        readOnly={true}
                                        extraCssClasses={' plan-card-format'}/>
                    </div>

                    {Body}
                </div>

                <div className={"row"}>
                    <div className={"col-12"}>
                        <div className={"flexSpaceBetween footerMargins"}>
                            <BackButton onClick={onBackClick}/>
                            <GenericButton onClick={onContinueClick}
                                           testid={'continueBtn'}
                                           disabled={!showContinueBtn || isCurrentCountMoreThanMaxCountOfSelectedLevel}
                                           text={i18n.common.continue}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UpdateSubscriptionPage;